import { MutableRefObject, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { notify } from "shell/store/notifications";
import { Sentry } from "utility/sentry";
import {
  InteractionMode,
  usesContentEditing,
  usesLayoutGrammar,
} from "./studioTypes";
import { CodeRegionReplaced } from "./useStudioAiEdit";

const bridgeInjectedCss = `
  .studio-hover {
    outline: 1px dashed #00bcd4;
    outline-offset: 2px;
    cursor: pointer;
  }
  .studio-selected {
    outline: 2px solid #ff9800;
    outline-offset: 2px;
  }
  .studio-layout-mode [data-layout-id] {
    cursor: pointer;
  }
  .studio-layout-mode [data-layout-id][draggable="true"] {
    cursor: move;
  }
  .studio-dragging {
    background-color: rgba(255, 152, 0, 0.18);
    outline: 2px solid #ff9800;
    color: transparent !important;
  }
  .studio-dragging * {
    visibility: hidden !important;
  }
  [data-layout-id].studio-drop-before {
    box-shadow:
      inset 0 4px 0 #ff9800,
      0 0 0 1px rgba(255, 152, 0, 0.35);
  }
  [data-layout-id].studio-drop-before-horizontal {
    box-shadow:
      inset 4px 0 0 #ff9800,
      0 0 0 1px rgba(255, 152, 0, 0.35);
  }
  [data-layout-id].studio-drop-after {
    box-shadow:
      inset 0 -4px 0 #ff9800,
      0 0 0 1px rgba(255, 152, 0, 0.35);
  }
  [data-layout-id].studio-drop-after-horizontal {
    box-shadow:
      inset -4px 0 0 #ff9800,
      0 0 0 1px rgba(255, 152, 0, 0.35);
  }
  [data-layout-id].studio-drop-inside {
    background-color: rgba(255, 152, 0, 0.18);
    outline: 2px dashed #ff9800;
    outline-offset: 2px;
  }
  .studio-static-editing {
    outline: 2px solid #ff9800 !important;
    outline-offset: 2px;
    cursor: text !important;
  }
  .studio-static-editing * {
    cursor: text !important;
  }
`;

type Args = {
  dispatch: (action: any) => any;
  interactionMode: InteractionMode;
  /** Whether this user may write view source. Gates the layout-write messages. */
  canEditLayout: boolean;
  /** Bound leaf resolved on the canvas — opens that field's content editor. */
  onDynamicEditRequest: (msg: {
    codeId?: string;
    layoutId?: string;
    studioId?: string;
    fieldZuid: string;
    fieldType?: string;
    itemZuid?: string;
    modelZuid?: string;
  }) => void;
  syncBridgeInteractionMode: (nextMode: InteractionMode) => void;
  postCommandToBridge: (cmd: any) => void;
  handleTemplateSourceMap: (msg: any) => void;
  handleReorderOutput: (msg: any) => void;
  handleLayoutContentUpdate: (msg: any) => void;
  handleLayersTree: (msg: any) => void;
  applyLayoutSelection: (next: {
    codeId?: string;
    layoutId?: string;
    breadcrumb?: { layoutId?: string; label: string }[];
  }) => void;
  clearLayoutSelection: () => void;
  applySelection: (next: {
    studioId?: string;
    fieldZuid: string;
    fieldType?: string;
    itemZuid?: string;
    modelZuid?: string;
  }) => void;
  fieldNameByZuid: Map<string, string>;
  currentHoverStudioIdRef: React.MutableRefObject<string | null>;
  clearSelection: () => void;
  previewReloadContinuationRef: MutableRefObject<null | (() => void)>;
  setIsNavigating: (value: boolean) => void;
  onBridgeFieldInput?: (fieldZuid: string) => void;
  onStaticEditImage?: (msg: {
    codeId: string;
    layoutId: string;
    isLeafImg: boolean;
    imgIndex: number;
    currentSrc: string;
  }) => void;
  /** True while an AI change is staged: the canvas is then its preview. */
  canvasLockedRef?: MutableRefObject<boolean>;
  /** The bridge's answer to a replaceCodeRegion command. */
  onCodeRegionReplaced?: (msg: CodeRegionReplaced) => void;
  /**
   * A freshly loaded canvas is ready, after the host's own setup posts.
   * `capabilities` lists optional bridge features; an older bridge sends none.
   */
  onBridgeReady?: (path?: string, capabilities?: string[]) => void;
  /**
   * A canvas keystroke in a bound field, before it is written to the store.
   * The canvas already shows it.
   */
  onBeforeBridgeContentInput?: (itemZuid: string, fieldName: string) => void;
  /** Escape on the canvas: an edit in progress there has ended. */
  onEscape?: () => void;
  /** Undo/redo pressed while focus was in the canvas. */
  onHistoryKey?: (action: "undo" | "redo") => void;
  /** The bridge's answer to historyRestore. */
  onHistoryRestored?: (msg: {
    stepId?: number;
    direction?: string;
    ok?: boolean;
    reason?: string;
  }) => void;
};

// Canvas gestures and the edits they stage. While an AI preview is showing,
// they would act on markup that is not the page's, so they are dropped.
const CANVAS_INPUT_MESSAGES = new Set([
  "DOM_EVENT",
  "REORDER_OUTPUT",
  "LAYOUT_CONTENT_UPDATE",
  "DYNAMIC_EDIT_REQUEST",
  "STATIC_EDIT_REJECTED",
  "STATIC_EDIT_IMAGE",
]);

export const useStudioBridge = ({
  dispatch,
  interactionMode,
  canEditLayout,
  onDynamicEditRequest,
  syncBridgeInteractionMode,
  postCommandToBridge,
  handleTemplateSourceMap,
  handleReorderOutput,
  handleLayoutContentUpdate,
  handleLayersTree,
  applyLayoutSelection,
  clearLayoutSelection,
  applySelection,
  fieldNameByZuid,
  currentHoverStudioIdRef,
  clearSelection,
  previewReloadContinuationRef,
  setIsNavigating,
  onBridgeFieldInput,
  onStaticEditImage,
  canvasLockedRef,
  onCodeRegionReplaced,
  onBridgeReady,
  onBeforeBridgeContentInput,
  onEscape,
  onHistoryKey,
  onHistoryRestored,
}: Args) => {
  const { t } = useTranslation();
  const handleBridgeReady = useCallback(
    (path?: string, capabilities?: string[]) => {
      postCommandToBridge({
        action: "injectCss",
        css: bridgeInjectedCss,
      });
      syncBridgeInteractionMode(interactionMode);
      postCommandToBridge({ action: "requestLayersTree" });
      onBridgeReady?.(path, capabilities);
    },
    [
      interactionMode,
      onBridgeReady,
      postCommandToBridge,
      syncBridgeInteractionMode,
    ]
  );

  const handleBridgeError = useCallback(
    (msg: any) => {
      const bridgeError = msg.error || {};
      const error = new Error(bridgeError.message || "Bridge error");
      if (bridgeError.stack) {
        (error as any).stack = bridgeError.stack;
      }

      dispatch(
        notify({
          kind: "error",
          message: t("content.studioPreviewError", {
            error: bridgeError.message || t("content.studioBridgeError"),
          }),
        })
      );

      Sentry.withScope((scope) => {
        scope.setLevel("error");
        scope.setTag("bridge.kind", msg.kind || "error");
        scope.setContext("bridge.error", {
          filename: bridgeError.filename || null,
          lineno: bridgeError.lineno || null,
          colno: bridgeError.colno || null,
          stack: bridgeError.stack || null,
        });
        scope.setExtra("bridge.message", bridgeError.message || "");
        Sentry.captureException(error);
      });
    },
    [dispatch, t]
  );

  const handleBridgeDomEvent = useCallback(
    (msg: any) => {
      const { eventType, element, value, breadcrumb } = msg;
      if (!element) return;

      const dataset: Record<string, string> =
        (element.dataset as Record<string, string>) || {};
      const studioId = dataset.studioId;
      const fieldZuid: string | undefined = dataset.fieldZuid;
      const fieldType = dataset.fieldType;
      const itemZuid = dataset.itemZuid;
      const modelZuid = dataset.modelZuid;
      const layoutId = dataset.layoutId;
      const codeId = dataset.codeId;

      switch (eventType) {
        case "mousedown":
        case "dblclick": {
          if (!usesLayoutGrammar(interactionMode)) return;
          applyLayoutSelection({ codeId, layoutId, breadcrumb });
          return;
        }

        case "escape": {
          onEscape?.();
          if (usesLayoutGrammar(interactionMode)) {
            clearLayoutSelection();
            return;
          }
          clearSelection();
          return;
        }

        case "click": {
          if (usesLayoutGrammar(interactionMode)) return;
          if (!fieldZuid) return;

          applySelection({
            studioId,
            fieldZuid,
            fieldType,
            itemZuid,
            modelZuid,
          });
          return;
        }

        case "input": {
          if (!usesContentEditing(interactionMode) || !fieldZuid || !itemZuid)
            return;
          const fieldName = fieldNameByZuid.get(fieldZuid);
          if (!fieldName) return;

          onBeforeBridgeContentInput?.(itemZuid, fieldName);
          dispatch({
            type: "SET_ITEM_DATA",
            itemZUID: itemZuid,
            key: fieldName,
            value: typeof value === "string" ? value : "",
          });
          onBridgeFieldInput?.(fieldZuid);
          return;
        }

        case "mouseover": {
          if (usesLayoutGrammar(interactionMode)) return;
          if (!studioId) return;
          currentHoverStudioIdRef.current = studioId;
          postCommandToBridge({
            action: "addClass",
            studioId,
            className: "studio-hover",
            itemZuid,
          });
          return;
        }

        case "mouseout": {
          if (usesLayoutGrammar(interactionMode)) return;
          if (!studioId) return;
          if (currentHoverStudioIdRef.current === studioId) {
            currentHoverStudioIdRef.current = null;
          }
          postCommandToBridge({
            action: "removeClass",
            studioId,
            className: "studio-hover",
            itemZuid,
          });
        }
      }
    },
    [
      applyLayoutSelection,
      applySelection,
      clearLayoutSelection,
      currentHoverStudioIdRef,
      dispatch,
      fieldNameByZuid,
      interactionMode,
      postCommandToBridge,
      onBridgeFieldInput,
      onBeforeBridgeContentInput,
      onEscape,
    ]
  );

  const handlePreviewLoad = useCallback(() => {
    setIsNavigating(false);
    const continuation = previewReloadContinuationRef.current;
    previewReloadContinuationRef.current = null;
    continuation?.();
  }, [previewReloadContinuationRef, setIsNavigating]);

  useEffect(() => {
    function handleMessage(evt: MessageEvent<any>) {
      const data = evt.data;
      if (!data || data.source !== "studio-bridge") {
        return;
      }

      const msg = data.message;
      if (!msg) return;

      if (msg.type === "CODE_REGION_REPLACED") {
        onCodeRegionReplaced?.(msg);
        return;
      }

      // Not canvas input: the bridge sends both while a preview is locked
      // too, and undo is how a locked AI preview is taken back.
      if (msg.type === "HISTORY_KEY") {
        if (msg.action === "undo" || msg.action === "redo") {
          onHistoryKey?.(msg.action);
        }
        return;
      }

      if (msg.type === "HISTORY_RESTORED") {
        onHistoryRestored?.(msg);
        return;
      }

      if (canvasLockedRef?.current && CANVAS_INPUT_MESSAGES.has(msg.type)) {
        return;
      }

      if (msg.type === "TEMPLATE_SOURCE_MAP") {
        handleTemplateSourceMap(msg);
        return;
      }

      if (msg.type === "BRIDGE_READY") {
        handleBridgeReady(
          typeof msg.path === "string" ? msg.path : undefined,
          Array.isArray(msg.capabilities)
            ? msg.capabilities.filter(
                (capability: unknown): capability is string =>
                  typeof capability === "string"
              )
            : undefined
        );
        return;
      }

      if (msg.type === "BRIDGE_ERROR") {
        handleBridgeError(msg);
        return;
      }

      if (msg.type === "DOM_EVENT") {
        handleBridgeDomEvent(msg);
        return;
      }

      // Both of these stage a write to view source. They arrive as window
      // messages from the preview, so the mode toggle is not a gate on them:
      // anything that can postMessage can send them. Refuse without code
      // capability rather than relying on the UI never offering the mode.
      if (msg.type === "REORDER_OUTPUT") {
        if (!canEditLayout) return;
        handleReorderOutput(msg);
        return;
      }

      if (msg.type === "LAYOUT_CONTENT_UPDATE") {
        if (!canEditLayout) return;
        handleLayoutContentUpdate(msg);
        return;
      }

      // Studio mode only: the bridge resolved a positive field binding at the
      // leaf, so open that field's content editor. The same selection path a
      // layers-row click takes — no new selection machinery.
      if (msg.type === "DYNAMIC_EDIT_REQUEST") {
        // The bridge is binary and posts this from both the canvas and the
        // layers panel without knowing the mode. Layout is the only mode that
        // can reach a bound leaf and not edit it, so it is the only one that
        // gets redirected — telling a content-mode user to switch to content
        // mode would be nonsense. The deployed bridge cannot send this in
        // content wire mode today; this is the same stance the layout-write
        // handlers above take, that anything able to postMessage can send it.
        if (!usesContentEditing(interactionMode)) {
          dispatch(
            notify({
              kind: "warn",
              message: t("content.studioStaticEditRejected"),
            })
          );
          return;
        }
        if (!msg.fieldZuid) return;

        onDynamicEditRequest({
          codeId: msg.codeId,
          layoutId: msg.layoutId,
          studioId: msg.studioId,
          fieldZuid: msg.fieldZuid,
          fieldType: msg.fieldType,
          itemZuid: msg.itemZuid,
          modelZuid: msg.modelZuid,
        });
        return;
      }

      if (msg.type === "LAYERS_TREE") {
        handleLayersTree(msg);
        return;
      }

      if (msg.type === "STATIC_EDIT_REJECTED") {
        // The bridge refuses a leaf that is neither field-bound nor matching its
        // template; no mode switch helps, so one message fits every mode.
        dispatch(
          notify({
            kind: "warn",
            message: t("content.studioStaticEditTemplateMismatch"),
          })
        );
        return;
      }

      if (msg.type === "STATIC_EDIT_IMAGE") {
        onStaticEditImage?.(msg);
        return;
      }
    }

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    applySelection,
    canEditLayout,
    onDynamicEditRequest,
    clearSelection,
    handleBridgeDomEvent,
    handleBridgeError,
    handleBridgeReady,
    handleLayersTree,
    handleLayoutContentUpdate,
    handleReorderOutput,
    handleTemplateSourceMap,
    interactionMode,
    onCodeRegionReplaced,
    onHistoryKey,
    onHistoryRestored,
    onStaticEditImage,
    canvasLockedRef,
    t,
  ]);

  return {
    handlePreviewLoad,
  };
};
